import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { EXPORT_JOB_DRIVERS } from './exportJobDrivers'
import type {
  ExportJob,
  ExportJobKind,
  ExportJobPatch,
  ExportJobStatus,
} from './exportJobs.types'

/**
 * The product's single export centre.
 *
 * Any feature that starts a server-side job producing a file registers it here
 * and the shared UI takes over: the dock summarises it, the transfers drawer
 * lists it, and the file is downloadable from either. Polling, retries,
 * persistence and cleanup happen once, in this store, for every kind of export.
 *
 * Unfinished jobs are mirrored to `localStorage` so a page reload doesn't lose
 * sight of an encode that is still running server-side — without that, the user
 * assumes it crashed and launches it again, paying for the same encode twice.
 * A job that finishes while the tab is closed is still stored as "running", so
 * the next boot polls it, finds it ready and hands the user their file.
 *
 * Finished jobs survive a reload too, but only for {@link TERMINAL_RETENTION_MS}
 * — long enough to cover an accidental refresh or a navigation right after the
 * file lands, short enough that the drawer never turns into a graveyard. Past
 * that window they are dropped: the file itself already lives in the media
 * library.
 */

const DEFAULT_POLL_INTERVAL_MS = 2000
const MAX_CONSECUTIVE_FAILURES = 5
const HISTORY_LIMIT = 20
const STORAGE_KEY = 'videas.exportJobs.v1'
/** Beyond this, a restored job is stale enough that its file has likely expired. */
const STORAGE_MAX_AGE_MS = 24 * 60 * 60 * 1000
/** How long a finished export stays available after a reload. */
const TERMINAL_RETENTION_MS = 10 * 60 * 1000

export interface ExportJobInput {
  id: string
  kind: ExportJobKind
  label: string
  meta?: Record<string, unknown>
  autoDownload?: boolean
}

/**
 * Download through a hidden iframe rather than a top-level navigation: export
 * URLs are served cross-origin and force `Content-Disposition: attachment`, so
 * `<a download>` is ignored, and navigating the top document would trip the
 * editors' unsaved-changes guards. A child frame never unloads the page.
 */
function triggerDownload(url: string): void {
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = url
  document.body.appendChild(iframe)
  setTimeout(() => iframe.remove(), 60000)
}

function isActiveStatus(status: ExportJobStatus): boolean {
  return status === 'pending' || status === 'running'
}

export const useExportJobsStore = defineStore('exportJobs', () => {
  const jobs = ref<ExportJob[]>([])

  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const failures = new Map<string, number>()

  const activeJobs = computed(() => jobs.value.filter((job) => isActiveStatus(job.status)))
  const activeCount = computed(() => activeJobs.value.length)
  const readyPending = computed(() =>
    jobs.value.filter((job) => job.status === 'ready' && !job.acknowledged),
  )
  const failedPending = computed(() =>
    jobs.value.filter((job) => job.status === 'failed' && !job.acknowledged),
  )
  const hasReady = computed(() => jobs.value.some((job) => job.status === 'ready'))
  const hasFailed = computed(() => jobs.value.some((job) => job.status === 'failed'))

  /** Average across running jobs — what the dock's ring shows. */
  const averageProgress = computed(() => {
    const list = activeJobs.value
    if (list.length === 0) return 0
    const sum = list.reduce((acc, job) => acc + (job.progress ?? 0), 0)
    return Math.round(sum / list.length)
  })

  /** Distinct kinds currently running, so the dock can name the tools involved. */
  const activeKinds = computed(() => [...new Set(activeJobs.value.map((job) => job.kind))])

  function getJob(id: string): ExportJob | undefined {
    return jobs.value.find((job) => job.id === id)
  }

  // --- Persistence ---

  function persist(): void {
    try {
      const now = Date.now()
      const payload = jobs.value
        .filter(
          (job) =>
            isActiveStatus(job.status) ||
            (job.finishedAt !== null && now - job.finishedAt <= TERMINAL_RETENTION_MS),
        )
        // A link that expires is not kept: a restored job mints a fresh one instead.
        .map((job) => (EXPORT_JOB_DRIVERS[job.kind]?.resolveDownloadUrl ? { ...job, downloadUrl: null } : job))
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // Private browsing or a full quota: losing the mirror is not worth an error.
    }
  }

  function restore(): void {
    let stored: unknown
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      stored = JSON.parse(raw)
    } catch {
      return
    }
    if (!Array.isArray(stored)) return

    const now = Date.now()
    for (const entry of stored as ExportJob[]) {
      if (!entry?.id || !entry.kind) continue
      if (getJob(entry.id)) continue
      // A kind with no driver is dropped rather than restored: nothing could poll
      // it to an end, and a finished one's file was served by a backend no longer
      // reached.
      if (!EXPORT_JOB_DRIVERS[entry.kind]) continue

      // A file that is already ready must not download itself during page load
      // — the user gets a button instead, whatever the job's original setting.
      if (entry.status === 'ready' || entry.status === 'failed') {
        if (now - (entry.finishedAt ?? 0) > TERMINAL_RETENTION_MS) continue
        // `acknowledged` is kept as stored: an export the user already
        // downloaded stays in the drawer without popping the dock again.
        jobs.value.push({ ...entry, autoDownload: false })
        if (entry.status === 'ready') void refreshLink(entry.id)
        continue
      }

      if (now - (entry.startedAt ?? 0) > STORAGE_MAX_AGE_MS) continue
      jobs.value.push({
        ...entry,
        status: 'running',
        autoDownload: false,
        acknowledged: false,
        error: null,
      })
      schedule(entry.id, 0)
    }
    persist()
  }

  // --- Polling ---

  function schedule(id: string, delay: number): void {
    clearTimer(id)
    timers.set(
      id,
      setTimeout(() => {
        timers.delete(id)
        void tick(id)
      }, delay),
    )
  }

  function clearTimer(id: string): void {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }
  }

  async function tick(id: string): Promise<void> {
    const job = getJob(id)
    if (!job || !isActiveStatus(job.status)) return

    const driver = EXPORT_JOB_DRIVERS[job.kind]
    if (!driver) {
      applyPatch(id, {
        status: 'failed',
        error: { key: 'assets.transfers.exports.errorGeneric' },
      })
      return
    }

    try {
      const patch = await driver.poll(job)
      failures.set(id, 0)
      applyPatch(id, patch)
    } catch {
      const count = (failures.get(id) ?? 0) + 1
      failures.set(id, count)
      if (count >= MAX_CONSECUTIVE_FAILURES) {
        applyPatch(id, {
          status: 'failed',
          error: { key: 'assets.transfers.exports.errorNetwork' },
        })
        return
      }
    }

    const current = getJob(id)
    if (!current || !isActiveStatus(current.status)) return
    schedule(id, driver.intervalMs ?? DEFAULT_POLL_INTERVAL_MS)
  }

  function applyPatch(id: string, patch: ExportJobPatch): void {
    const job = getJob(id)
    if (!job) return
    Object.assign(job, patch)

    if (job.status === 'ready' || job.status === 'failed') {
      clearTimer(id)
      failures.delete(id)
      // Stamped once: a restored job keeps the time it actually finished, so
      // the retention window doesn't restart on every reload.
      if (job.finishedAt === null) job.finishedAt = Date.now()

      if (job.status === 'ready' && job.autoDownload && job.downloadUrl) {
        triggerDownload(job.downloadUrl)
        // The browser already has the file: no need to nag from the dock.
        job.acknowledged = true
      }
    }

    persist()
  }

  // --- Public API ---

  /** Add a job the caller has just started server-side, and follow it. */
  function register(input: ExportJobInput): ExportJob {
    const existing = getJob(input.id)
    if (existing) return existing

    const job: ExportJob = {
      id: input.id,
      kind: input.kind,
      label: input.label,
      status: 'pending',
      progress: 0,
      phase: null,
      detail: null,
      error: null,
      filename: null,
      sizeBytes: null,
      downloadUrl: null,
      autoDownload: input.autoDownload ?? false,
      acknowledged: false,
      startedAt: Date.now(),
      finishedAt: null,
      meta: input.meta ?? {},
    }
    jobs.value.push(job)

    // Keep the finished ones from piling up while never dropping a live job.
    if (jobs.value.length > HISTORY_LIMIT) {
      const overflow = jobs.value.length - HISTORY_LIMIT
      const removable = jobs.value.filter((candidate) => !isActiveStatus(candidate.status))
      removable.slice(0, overflow).forEach((candidate) => remove(candidate.id))
    }

    schedule(job.id, 0)
    persist()
    return job
  }

  function update(id: string, patch: ExportJobPatch): void {
    applyPatch(id, patch)
  }

  /** A fresh link for a finished job whose kind mints expiring ones; the stored one otherwise. */
  async function currentLink(job: ExportJob): Promise<string | null> {
    const resolve = EXPORT_JOB_DRIVERS[job.kind]?.resolveDownloadUrl
    if (!resolve) return job.downloadUrl
    return resolve(job).catch(() => null)
  }

  /** Give a restored finished job a link again, which is what lets the drawer offer its file. */
  async function refreshLink(id: string): Promise<void> {
    const job = getJob(id)
    if (!job || !EXPORT_JOB_DRIVERS[job.kind]?.resolveDownloadUrl) return
    const url = await currentLink(job)
    if (url) job.downloadUrl = url
  }

  async function download(id: string): Promise<void> {
    const job = getJob(id)
    if (!job || job.status !== 'ready') return
    const url = await currentLink(job)
    if (!url) return
    triggerDownload(url)
    job.acknowledged = true
    persist()
  }

  /** Dismiss from the dock; the job stays visible in the drawer. */
  function acknowledge(id: string): void {
    const job = getJob(id)
    if (!job) return
    job.acknowledged = true
    persist()
  }

  function acknowledgeAll(): void {
    jobs.value.forEach((job) => {
      if (!isActiveStatus(job.status)) job.acknowledged = true
    })
    persist()
  }

  async function retry(id: string): Promise<boolean> {
    const job = getJob(id)
    if (!job) return false
    const driver = EXPORT_JOB_DRIVERS[job.kind]
    if (!driver?.restart) return false

    try {
      const restarted = await driver.restart(job)
      if (!restarted) return false

      clearTimer(id)
      failures.delete(id)
      job.id = restarted.id
      if (restarted.meta) job.meta = restarted.meta
      job.status = 'pending'
      job.progress = 0
      job.phase = null
      job.detail = null
      job.error = null
      job.downloadUrl = null
      job.filename = null
      job.sizeBytes = null
      job.acknowledged = false
      job.startedAt = Date.now()
      job.finishedAt = null

      schedule(job.id, 0)
      persist()
      return true
    } catch {
      return false
    }
  }

  function remove(id: string): void {
    clearTimer(id)
    failures.delete(id)
    const index = jobs.value.findIndex((job) => job.id === id)
    if (index !== -1) jobs.value.splice(index, 1)
    persist()
  }

  function clearByStatus(target: ExportJobStatus): void {
    jobs.value
      .filter((job) => job.status === target)
      .forEach((job) => remove(job.id))
  }

  function clearReady(): void {
    clearByStatus('ready')
  }

  function clearFailed(): void {
    clearByStatus('failed')
  }

  restore()

  return {
    jobs,
    activeJobs,
    activeCount,
    activeKinds,
    averageProgress,
    readyPending,
    failedPending,
    hasReady,
    hasFailed,
    getJob,
    register,
    update,
    download,
    acknowledge,
    acknowledgeAll,
    retry,
    remove,
    clearReady,
    clearFailed,
  }
})
